import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

export const getTransactionsFromMySQL = async (filters = {}) => {
    try {
        // Construir los parámetros de consulta a partir de los filtros
        const params = {};
        if (Array.isArray(filters.accountIds)) {
            params.accountIds = filters.accountIds.join(','); // Convertir array a cadena separada por comas
        } else if (typeof filters.accountIds === 'string') {
            params.accountIds = filters.accountIds; // Si ya es una cadena, usarla directamente
        }

        if (filters.ownerUid || filters.ownerId) params.ownerUid = filters.ownerUid || filters.ownerId; // Support both keys
        if (filters.startDate && filters.endDate) {
            params.startDate = filters.startDate; // Formato YYYY-MM-DD HH:mm:ss
            params.endDate = filters.endDate;
        }
        params.archived = 'false'; // Ensure we only get non-archived transactions by default

        // Realizar la solicitud GET
        const response = await apiClient.get(API_ENDPOINTS.transactions, { params });

        // Verificar si la solicitud fue exitosa
        if (response.data.success) {
            return response.data.transactions;
        } else {
            console.error('Error al obtener las transacciones:', response.data.message);
            throw new Error(response.data.message);
        }
    } catch (error) {
        console.error('Error al realizar la solicitud GET:', error.response?.data || error.message);
        throw error;
    }
};

export const createToMySQL = async (transaction, docRefId, currentUser, isGroupOwner) => {
    try {
        // Convertir la fecha al formato ISO si es necesario
        const formatDateToLocalMySQL = (dateInput) => {
            let d;
            if (dateInput?.seconds) {
                d = new Date(dateInput.seconds * 1000);
            } else if (dateInput instanceof Date) {
                d = dateInput;
            } else if (typeof dateInput === 'string') {
                d = new Date(dateInput);
            } else {
                throw new Error('Fecha inválida');
            }

            if (isNaN(d.getTime())) throw new Error('Fecha inválida');

            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const formattedDate = formatDateToLocalMySQL(transaction.date);

        // Datos a enviar al servidor
        const payload = {
            id: docRefId,
            accountId: transaction.accountId,
            destinationAccountId: transaction.destinationAccountId || null,
            categoryId: transaction.categoryId && transaction.categoryId !== '' ? transaction.categoryId : null,
            amount: transaction.amount,
            type: transaction.type,
            transactionType: transaction.transactionType,
            date: formattedDate, // Usar la fecha formateada
            description: transaction.description || '',
            ownerUid: currentUser.uid,
            status: transaction.status || 'aprobado', // Cambiar a 'aprobado' por defecto
            createdAt: transaction.createdAt || new Date().toISOString().replace('T', ' ').split('.')[0], // Formato MySQL
            updatedAt: transaction.updatedAt || new Date().toISOString().replace('T', ' ').split('.')[0], // Formato MySQL
        };

        // console.log('Datos enviados al servidor:', payload); // Registrar los datos enviados

        // Enviar la solicitud POST al servidor
        await apiClient.post(API_ENDPOINTS.transactions, payload);
        // console.log('Respuesta del servidor:', response.data);
        // console.log('Transacción guardada en MySQL');
    } catch (error) {
        console.error('Error al guardar la transacción en MySQL:', error.response?.data || error.message);
        throw error;
    }
};

export const transactionToMySQL = async (transaction, docRefId, currentUser, isGroupOwner) => {
    try {
        // Convertir la fecha al formato ISO si es necesario
        const formatDateToLocalMySQL = (dateInput) => {
            let d;
            if (dateInput?.seconds) {
                d = new Date(dateInput.seconds * 1000);
            } else if (dateInput instanceof Date) {
                d = dateInput;
            } else if (typeof dateInput === 'string') {
                d = new Date(dateInput);
            } else {
                throw new Error('Fecha inválida');
            }

            if (isNaN(d.getTime())) throw new Error('Fecha inválida');

            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const formattedDate = formatDateToLocalMySQL(transaction.date);

        // Datos a enviar al servidor
        const payload = {
            id: docRefId,
            accountId: transaction.accountId,
            destinationAccountId: transaction.destinationAccountId || null,
            categoryId: transaction.categoryId && transaction.categoryId !== '' ? transaction.categoryId : null,
            amount: transaction.amount,
            type: transaction.type,
            date: formattedDate,
            description: transaction.description || '',
            ownerUid: transaction.ownerUid || currentUser?.uid, // Usar currentUser.uid si transaction.ownerUid no existe
            status: isGroupOwner ? 'aprobado' : 'pendiente',
            imageUrl: transaction.imageUrl || null, // Incluir la URL de la imagen
        };

        // console.log('Datos enviados al servidor:', payload);

        // Enviar la solicitud POST al servidor
        const response = await apiClient.post(API_ENDPOINTS.transactions, payload);
        // console.log('Transacción guardada en MySQL', response.data);
        return response.data;
    } catch (error) {
        console.error('Error al guardar la transacción en MySQL:', error.response?.data || error.message);
        throw error;
    }
};

export const updateToMySQL = async (transaction) => {
    try {
        // Convertir la fecha al formato ISO si es necesario
        // Helper to format date in local YYYY-MM-DD HH:mm:ss
        const formatDateToLocalMySQL = (dateInput) => {
            let d;
            if (dateInput?.seconds) {
                d = new Date(dateInput.seconds * 1000);
            } else if (dateInput instanceof Date) {
                d = dateInput;
            } else if (typeof dateInput === 'string') {
                d = new Date(dateInput);
            } else {
                throw new Error('Fecha inválida');
            }

            if (isNaN(d.getTime())) throw new Error('Fecha inválida');

            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const formattedDate = formatDateToLocalMySQL(transaction.date);

        // Datos a enviar al servidor
        const payload = {
            id: transaction.id,
            accountId: transaction.accountId,
            destinationAccountId: transaction.destinationAccountId || null,
            categoryId: transaction.categoryId && transaction.categoryId !== '' ? transaction.categoryId : null,
            amount: transaction.amount,
            type: transaction.type,
            date: formattedDate,
            description: transaction.description || '',
            ownerUid: transaction.ownerUid, // Asegurarse de incluir ownerUid
            status: transaction.status,
            imageUrl: transaction.imageUrl || null, // Incluir la URL de la imagen
            updatedAt: new Date().toISOString().replace('T', ' ').split('.')[0],
        };

        // console.log('Datos enviados al servidor para actualizar:', payload);

        // Enviar la solicitud PUT al servidor
        const response = await apiClient.put(API_ENDPOINTS.transactions, payload);
        return response.data;
    } catch (error) {
        console.error('Error al actualizar la transacción en MySQL:', error.response?.data || error.message);
        throw error;
    }
};

export const deleteTransactionFromMySQL = async (transactionId) => {
    try {
        // Enviar la solicitud DELETE al servidor
        const response = await apiClient.delete(API_ENDPOINTS.transactions, {
            data: { id: transactionId },
            headers: { 'Content-Type': 'application/json' },
        });

        // console.log('Transacción eliminada en MySQL:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error al eliminar la transacción en MySQL:', error.response?.data || error.message);
        throw error; // Lanzar el error para manejarlo en el lugar donde se llame esta función
    }
};